import React, { useState, useCallback, useMemo } from 'react';
import {
  Typography,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { jaJP } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

import { lookupPostalCode, isValidPostalCodeFormat } from '../../../../lib/postal-code-service';

import type {
  BasicInformationSectionProps,
  PropertyClassificationCode,
  PropertyStructuralFormat,
  PropertyVehicleSize,
} from '../../types/property.types';

import { autoFillButtonStyles, PaperBoxStyles } from '@/components/styles/common-style';
import { getOptionKey, getStylesForEditableField } from '@/utils/helpers';
import {
  CustomDay,
  customCalendarHeaderSx,
  CustomCalendarHeaderStyles,
} from '@/components/layouts';
import {
  PUBLIC_PRIVATE_CLASSIFICATION_OPTIONS,
  STRUCTURAL_FORMAT_OPTIONS,
  VEHICLE_SIZE_OPTIONS,
} from '@/lib/constants';

export const BasicInformationSection: React.FC<BasicInformationSectionProps> = ({
  formData,
  originalFormData,
  onUpdateField,
  getFieldError,
  isFieldValid,
  customers,
  constructionCompanies,
  companiesLoading,
}) => {
  const [isLookingUp, setIsLookingUp] = useState(false);

  // Transform customers and construction companies to autocomplete options format
  const customerOptions = useMemo(() => {
    if (!Array.isArray(customers) || customers.length === 0) {
      return [];
    }
    const options = customers.map((customer) => ({
      key: customer.id,
      value: customer.id,
      label: customer.name || customer.abbreviationName || customer.formalName || '',
      data: customer,
    }));

    return options;
  }, [customers]);

  const constructionCompanyOptions = useMemo(() => {
    if (!Array.isArray(constructionCompanies) || constructionCompanies.length === 0) {
      return [];
    }
    const options = constructionCompanies.map((company) => ({
      key: company.id,
      value: company.id,
      label: company.name || company.nameAbbreviation || '',
      data: company,
    }));
    return options;
  }, [constructionCompanies]);

  // Find selected customer from formData
  const selectedCustomer = useMemo(() => {
    if (!formData.customer_id || customerOptions.length === 0) {
      return null;
    }
    const found =
      customerOptions.find(
        (opt) =>
          opt.value === formData.customer_id || opt.value === formData.customer_id?.toString()
      ) || null;
    if (!found && formData.customer) {
      return {
        key: formData.customer.id,
        value: formData.customer.id,
        label:
          formData.customer.name ||
          formData.customer.abbreviationName ||
          formData.customer.formalName ||
          '',
        data: formData.customer,
      };
    }
    return found;
  }, [formData.customer_id, customerOptions]);

  // Find selected construction company from formData
  const selectedConstructionCompany = useMemo(() => {
    if (!formData.construction_company_id || constructionCompanyOptions.length === 0) {
      return null;
    }
    const found =
      constructionCompanyOptions.find(
        (opt) =>
          opt.value === formData.construction_company_id ||
          opt.value === formData.construction_company_id?.toString()
      ) || null;
    if (!found && formData.construction_company) {
      return {
        key: formData.construction_company.id,
        value: formData.construction_company.id,
        label:
          formData.construction_company.name ||
          formData.construction_company.nameAbbreviation ||
          '',
        data: formData.construction_company,
      };
    }

    return found;
  }, [formData.construction_company_id, constructionCompanyOptions]);

  // Handle customer change
  const handleCustomerChange = useCallback(
    (newValue: { value: string | number } | null) => {
      onUpdateField('customer_id', newValue ? newValue.value : '');
    },
    [onUpdateField]
  );

  // Handle construction company change
  const handleConstructionCompanyChange = useCallback(
    (newValue: { value: string | number } | null) => {
      onUpdateField('construction_company_id', newValue ? newValue.value : '');
    },
    [onUpdateField]
  );

  // Postal code lookup handler
  const handlePostalCodeLookup = useCallback(async () => {
    const postalCode1 = formData.postal_code_first_half?.toString() || '';
    const postalCode2 = formData.postal_code_latter_half?.toString() || '';

    if (!isValidPostalCodeFormat(postalCode1, postalCode2)) {
      return;
    }

    setIsLookingUp(true);
    try {
      const result = await lookupPostalCode(postalCode1, postalCode2);
      if (result) {
        onUpdateField('address', result.fullAddress);
      }
    } catch (error) {
      console.error('Error looking up postal code:', error);
    } finally {
      setIsLookingUp(false);
    }
  }, [formData.postal_code_first_half, formData.postal_code_latter_half, onUpdateField]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale='ja'
      localeText={jaJP.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <CustomCalendarHeaderStyles />
      <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
        <Typography
          variant='h5'
          fontWeight='bold'
          sx={{
            mb: 3,
            color: '#1A1C20',
            fontSize: '22px',
            fontWeight: 600,
          }}
        >
          現場基本情報
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* WAO現場コード */}
          <TextField
            fullWidth
            label='WAO現場コード'
            placeholder='WAO現場コードを入力'
            value={formData.wao_property_code || ''}
            onChange={(e) => onUpdateField('wao_property_code', e.target.value)}
            error={!isFieldValid('wao_property_code')}
            helperText={getFieldError('wao_property_code')}
            slotProps={{
              input: {
                inputProps: { maxLength: 30 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.wao_property_code,
                  formData.wao_property_code
                ),
              },
            }}
          />

          {/* 現場 */}
          <TextField
            fullWidth
            required
            label='現場'
            placeholder='現場を入力'
            value={formData.property_name || ''}
            onChange={(e) => onUpdateField('property_name', e.target.value)}
            error={!isFieldValid('property_name')}
            helperText={getFieldError('property_name')}
            slotProps={{
              input: {
                inputProps: { maxLength: 200 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.property_name,
                  formData.property_name
                ),
              },
            }}
          />

          {/* 現場略称* */}
          <TextField
            fullWidth
            required
            label='現場略称'
            placeholder='現場略称を入力'
            value={formData.property_name_abbreviation || ''}
            onChange={(e) => onUpdateField('property_name_abbreviation', e.target.value)}
            error={!isFieldValid('property_name_abbreviation')}
            helperText={getFieldError('property_name_abbreviation')}
            slotProps={{
              input: {
                inputProps: { maxLength: 100 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.property_name_abbreviation,
                  formData.property_name_abbreviation
                ),
              },
            }}
          />

          {/* 郵便番号 */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
            <TextField
              label='郵便番号 (前半)'
              placeholder='郵便番号 (前半)'
              value={formData.postal_code_first_half || ''}
              onChange={(e) => onUpdateField('postal_code_first_half', e.target.value)}
              error={!isFieldValid('postal_code_first_half')}
              helperText={getFieldError('postal_code_first_half')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 3, pattern: '[0-9]*', inputMode: 'numeric' },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.postal_code_first_half,
                    formData.postal_code_first_half
                  ),
                },
              }}
              sx={{
                width: 315,
              }}
            />
            <TextField
              label='郵便番号 (後半)'
              placeholder='郵便番号 (後半)'
              value={formData.postal_code_latter_half || ''}
              onChange={(e) => onUpdateField('postal_code_latter_half', e.target.value)}
              error={!isFieldValid('postal_code_latter_half')}
              helperText={getFieldError('postal_code_latter_half')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 4, pattern: '[0-9]*', inputMode: 'numeric' },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.postal_code_latter_half,
                    formData.postal_code_latter_half
                  ),
                },
              }}
              sx={{ width: 315 }}
            />
            <Button
              variant='outlined'
              onClick={() => {
                void handlePostalCodeLookup();
              }}
              disabled={
                !isValidPostalCodeFormat(
                  formData.postal_code_first_half?.toString() || '',
                  formData.postal_code_latter_half?.toString() || ''
                ) ||
                isLookingUp ||
                // Disable if only one field has value
                (!!formData.postal_code_first_half && !formData.postal_code_latter_half) ||
                (!formData.postal_code_first_half && !!formData.postal_code_latter_half)
              }
              startIcon={isLookingUp ? <CircularProgress size={16} /> : <SearchIcon />}
              sx={{ ...autoFillButtonStyles, width: '20%' }}
            >
              {isLookingUp ? '検索中...' : '住所自動入力'}
            </Button>
          </Box>

          {/* 住所 */}
          <TextField
            fullWidth
            required
            label='住所'
            placeholder='住所を入力'
            value={formData.address || ''}
            onChange={(e) => onUpdateField('address', e.target.value)}
            error={!isFieldValid('address')}
            helperText={getFieldError('address')}
            slotProps={{
              input: {
                inputProps: { maxLength: 200 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.address,
                  formData.address
                ),
              },
            }}
          />

          {/* 開始日 */}
          <DatePicker
            label='開始日'
            format='YYYY/MM/DD'
            value={formData.start_date ? dayjs(formData.start_date) : null}
            onChange={(newValue) => {
              onUpdateField('start_date', newValue ? newValue.format('YYYY-MM-DD') : '');
            }}
            slots={{
              day: CustomDay,
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !isFieldValid('start_date'),
                helperText: getFieldError('start_date'),
                InputProps: {
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.start_date,
                    formData.start_date
                  ),
                },
              },
              calendarHeader: {
                sx: customCalendarHeaderSx,
              },
            }}
          />

          {/* 施主名 */}
          <TextField
            fullWidth
            label='施主'
            placeholder='施主を入力'
            value={formData.owner_name || ''}
            onChange={(e) => onUpdateField('owner_name', e.target.value)}
            error={!isFieldValid('owner_name')}
            helperText={getFieldError('owner_name')}
            slotProps={{
              input: {
                inputProps: { maxLength: 100 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.owner_name,
                  formData.owner_name
                ),
              },
            }}
          />

          {/* 官民区分 */}
          <FormControl fullWidth error={!isFieldValid('public_private_classification_code')}>
            <InputLabel>官民区分</InputLabel>
            <Select
              value={
                formData.public_private_classification_code
                  ? formData.public_private_classification_code.toString()
                  : ''
              }
              onChange={(e) =>
                onUpdateField(
                  'public_private_classification_code',
                  e.target.value ? (Number(e.target.value) as PropertyClassificationCode) : ''
                )
              }
              label='官民区分'
              slotProps={{
                input: {
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.public_private_classification_code,
                    formData.public_private_classification_code
                  ),
                },
              }}
            >
              <MenuItem value=''>官民区分を選択</MenuItem>
              {PUBLIC_PRIVATE_CLASSIFICATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('public_private_classification_code')}</FormHelperText>
          </FormControl>

          {/* 得意先 */}
          <Autocomplete
            options={customerOptions}
            getOptionKey={getOptionKey}
            loading={companiesLoading}
            value={selectedCustomer}
            onChange={(_, newValue) => handleCustomerChange(newValue)}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label='得意先'
                placeholder='得意先を入力'
                required
                error={!isFieldValid('customer_id')}
                helperText={getFieldError('customer_id')}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {companiesLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.customer_id,
                      formData.customer_id
                    ),
                  },
                }}
              />
            )}
            noOptionsText='該当する得意先が見つかりません'
          />

          {/* 建設会社 */}
          <Autocomplete
            options={constructionCompanyOptions}
            getOptionKey={getOptionKey}
            loading={companiesLoading}
            value={selectedConstructionCompany}
            onChange={(_, newValue) => handleConstructionCompanyChange(newValue)}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label='建設会社'
                placeholder='建設会社を入力'
                required
                error={!isFieldValid('construction_company_id')}
                helperText={getFieldError('construction_company_id')}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {companiesLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.construction_company_id,
                      formData.construction_company_id
                    ),
                  },
                }}
              />
            )}
            noOptionsText='該当する建設会社が見つかりません'
          />

          {/* 工期 */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <DatePicker
              label='工期'
              format='YYYY/MM/DD'
              value={
                formData.construction_period_from ? dayjs(formData.construction_period_from) : null
              }
              onChange={(newValue) => {
                onUpdateField(
                  'construction_period_from',
                  newValue ? newValue.format('YYYY-MM-DD') : ''
                );
              }}
              maxDate={
                formData.construction_period_to ? dayjs(formData.construction_period_to) : undefined
              }
              slots={{
                day: CustomDay,
              }}
              slotProps={{
                textField: {
                  error: !isFieldValid('construction_period_from'),
                  helperText: getFieldError('construction_period_from'),
                  InputProps: {
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.construction_period_from,
                      formData.construction_period_from
                    ),
                  },
                  sx: {
                    flex: 1,
                    '& .MuiFormHelperText-root': {
                      minHeight: '20px',
                      marginTop: '3px',
                    },
                  },
                },
                calendarHeader: {
                  sx: customCalendarHeaderSx,
                },
              }}
            />
            <Typography variant='body1' sx={{ mx: 1, mt: '14px' }}>
              ~
            </Typography>
            <DatePicker
              label='工期'
              format='YYYY/MM/DD'
              value={
                formData.construction_period_to ? dayjs(formData.construction_period_to) : null
              }
              onChange={(newValue) => {
                onUpdateField(
                  'construction_period_to',
                  newValue ? newValue.format('YYYY-MM-DD') : ''
                );
              }}
              minDate={
                formData.construction_period_from
                  ? dayjs(formData.construction_period_from)
                  : undefined
              }
              slots={{
                day: CustomDay,
              }}
              slotProps={{
                textField: {
                  error: !isFieldValid('construction_period_to'),
                  helperText: getFieldError('construction_period_to'),
                  InputProps: {
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.construction_period_to,
                      formData.construction_period_to
                    ),
                  },
                  sx: {
                    flex: 1,
                    '& .MuiFormHelperText-root': {
                      minHeight: '20px',
                      marginTop: '3px',
                    },
                  },
                },
                calendarHeader: {
                  sx: customCalendarHeaderSx,
                },
              }}
            />
          </Box>

          {/* 事務所住所 */}
          <TextField
            fullWidth
            label='事務所住所'
            placeholder='事務所住所を入力'
            value={formData.office_address || ''}
            onChange={(e) => onUpdateField('office_address', e.target.value)}
            error={!isFieldValid('office_address')}
            helperText={getFieldError('office_address')}
            slotProps={{
              input: {
                inputProps: { maxLength: 200 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.office_address,
                  formData.office_address
                ),
              },
            }}
          />

          {/* 事務所TEL & FAX */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              label='事務所TEL'
              placeholder='事務所電話番号を入力'
              value={formData.office_tel || ''}
              onChange={(e) => onUpdateField('office_tel', e.target.value)}
              error={!isFieldValid('office_tel')}
              helperText={getFieldError('office_tel')}
              slotProps={{
                input: {
                  inputProps: {
                    maxLength: 30,
                  },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.office_tel,
                    formData.office_tel
                  ),
                },
              }}
            />
            <TextField
              fullWidth
              label='事務所FAX'
              placeholder='事務所FAX番号を入力'
              value={formData.office_fax || ''}
              onChange={(e) => onUpdateField('office_fax', e.target.value)}
              error={!isFieldValid('office_fax')}
              helperText={getFieldError('office_fax')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 30 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.office_fax,
                    formData.office_fax
                  ),
                },
              }}
            />
          </Box>

          {/* 構造形式 */}
          <FormControl fullWidth error={!isFieldValid('structural_format_code')}>
            <InputLabel>構造形式</InputLabel>
            <Select
              value={
                formData.structural_format_code ? formData.structural_format_code.toString() : ''
              }
              onChange={(e) =>
                onUpdateField(
                  'structural_format_code',
                  e.target.value ? (Number(e.target.value) as PropertyStructuralFormat) : ''
                )
              }
              label='構造形式'
              slotProps={{
                input: {
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.structural_format_code,
                    formData.structural_format_code
                  ),
                },
              }}
            >
              <MenuItem value=''>構造形式を選択</MenuItem>
              {STRUCTURAL_FORMAT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('structural_format_code')}</FormHelperText>
          </FormControl>

          {/* 構造形式（その他） - only show if "その他" is selected */}
          {formData.structural_format_code === 5 && (
            <TextField
              fullWidth
              label='構造形式（その他）'
              placeholder='構造形式（その他）を入力'
              value={formData.structural_format_others || ''}
              onChange={(e) => onUpdateField('structural_format_others', e.target.value)}
              error={!isFieldValid('structural_format_others')}
              helperText={getFieldError('structural_format_others')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 20 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.structural_format_others,
                    formData.structural_format_others
                  ),
                },
              }}
            />
          )}

          {/* 階数 */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label='階数地上'
              placeholder='階数地上'
              value={formData.number_of_floors_above_ground || ''}
              onChange={(e) => onUpdateField('number_of_floors_above_ground', e.target.value)}
              error={!isFieldValid('number_of_floors_above_ground')}
              helperText={getFieldError('number_of_floors_above_ground')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 9 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.number_of_floors_above_ground,
                    formData.number_of_floors_above_ground
                  ),
                },
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              label='階数地下'
              placeholder='階数地下'
              value={formData.number_of_basement_floors || ''}
              onChange={(e) => onUpdateField('number_of_basement_floors', e.target.value)}
              error={!isFieldValid('number_of_basement_floors')}
              helperText={getFieldError('number_of_basement_floors')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 9 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.number_of_basement_floors,
                    formData.number_of_basement_floors
                  ),
                },
              }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* 生コンクリート契約数量 */}
          <TextField
            fullWidth
            label='生コンクリート契約数量'
            placeholder='生コンクリート契約数量を入力'
            value={formData.ready_mixed_concrete_contract_quantity || ''}
            onChange={(e) =>
              onUpdateField(
                'ready_mixed_concrete_contract_quantity',
                Number.parseInt(e.target.value) || 0
              )
            }
            error={!isFieldValid('ready_mixed_concrete_contract_quantity')}
            helperText={getFieldError('ready_mixed_concrete_contract_quantity')}
            slotProps={{
              input: {
                inputProps: { maxLength: 9, pattern: '[0-9]*', inputMode: 'numeric' },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.ready_mixed_concrete_contract_quantity,
                  formData.ready_mixed_concrete_contract_quantity
                ),
              },
            }}
          />

          {/* 車両サイズ */}
          <FormControl fullWidth error={!isFieldValid('vehicle_size')}>
            <InputLabel>車両サイズ</InputLabel>
            <Select
              value={formData.vehicle_size ? formData.vehicle_size.toString() : ''}
              onChange={(e) =>
                onUpdateField(
                  'vehicle_size',
                  e.target.value ? (Number(e.target.value) as PropertyVehicleSize) : ''
                )
              }
              label='車両サイズ'
              sx={getStylesForEditableField(
                !!originalFormData,
                originalFormData?.vehicle_size,
                formData.vehicle_size
              )}
            >
              <MenuItem value=''>車両サイズを選択</MenuItem>
              {VEHICLE_SIZE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('vehicle_size')}</FormHelperText>
          </FormControl>
        </Box>
      </PaperBoxStyles>
    </LocalizationProvider>
  );
};
